import { Button, Empty, Form, FormInstance, Input, Select, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useState } from "react";
import AddSquireIcon from "../../../../../assets/icons/AddSquireIcon";
import CustomDrawer from "../../../../../components/ui/CustomDrawer";
import FileUpload from "../../../../../components/ui/FileUpload";
import {
  useCreateServiceTypeMutation,
  useGetServiceTypesListQuery,
  useUpdateServiceTypeMutation,
} from "../../../../../features/Vendor/vendor.api";
import { ServiceTypeDetails } from "../../../../../features/Vendor/vendor.type";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import { validateEmail } from "../../../../../utils/validators";

const General = ({ form }: { form: FormInstance<any> }) => {
  const [typeForm] = Form.useForm();
  const { open, handleOpen, handleClose } = useModal<
    { label: string; value: number },
    "TYPE-MANAGE"
  >();

  // ------ States for service type ------
  const [isOpenTypes, setIsOpenTypes] = useState(false);
  const [typeSearch, setTypeSearch] = useState<string | null>(null);

  // ------ Watched Values ------
  const serviceType = Form.useWatch("service_type_id", form);

  console.log(serviceType);

  // ------ Queries ------
  const { data: serviceTypes } = useGetServiceTypesListQuery({
    params: { status: "active" },
  });

  // ------ Generated Select Options ------
  const typeOptions = serviceTypes?.data
    ?.map((serviceType: ServiceTypeDetails) => {
      return {
        label: serviceType.service_type_name,
        value: serviceType.service_type_id,
      };
    })
    .filter((item) =>
      typeSearch
        ? item.label.toLowerCase().includes(typeSearch?.toLowerCase())
        : item,
    );

  // ------ Mutations ------
  const [
    createServiceType,
    {
      isLoading: creating,
      isError: isCreateError,
      isSuccess: created,
      error: createError,
    },
  ] = useCreateServiceTypeMutation();
  const [
    updateServiceType,
    {
      isLoading: updating,
      isError: isUpdateError,
      isSuccess: updated,
      error: updateError,
    },
  ] = useUpdateServiceTypeMutation();

  // ------ Function for manage service type ------
  const handleFinish = (values: any) => {
    if (open.data?.value) {
      updateServiceType({ data: values, id: open.data.value });
    } else {
      createServiceType(values);
    }
  };

  // ------ Function for handle service type change ------
  const handleServiceTypeChange = (value: number) => {
    const prevServiceTypes = serviceType?.length ? serviceType : [serviceType];
    const findValue = prevServiceTypes?.find((type: number) => type === value);

    if (findValue) {
      form.setFieldValue(
        "service_type_id",
        prevServiceTypes?.filter((type: number) => type !== value),
      );
    } else {
      form.setFieldValue("service_type_id", [...prevServiceTypes, value]);
    }

    setIsOpenTypes(false);
  };

  // ------ Effects ------
  useEffect(() => {
    if (open.data) {
      typeForm.setFieldValue("service_type_name", open.data?.label);
    }
  }, [open, typeForm]);

  useEffect(() => {
    if (created || updated) {
      if (open.data?.value) {
        toast("success", "Service type updated successfully.");
      } else {
        toast("success", "Service type created successfully.");
      }
      typeForm.resetFields();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated, typeForm]);

  // ------ Errors ------
  useError(isCreateError, createError, "Service type create failed.");
  useError(isUpdateError, updateError, "Service type update failed.");
  return (
    <div className="p-5 rounded-xl border border-theme-indigo-50 mb-5">
      <div className="space-y-6">
        <h5 className="text-lg text-theme-gray-2 font-semibold">
          Vendor General Information
        </h5>
        <Form
          className="custom-form"
          layout="vertical"
          // requiredMark="optional"
          size="large"
          form={form}
        >
          <div className="grid grid-cols-3 gap-x-4">
            <Form.Item
              label="Business Name"
              name="business_name"
              rules={[
                { required: true, message: "Business name is required," },
              ]}
            >
              <Input placeholder="Enter business name" />
            </Form.Item>

            <Form.Item
              label="Service Type"
              name="service_type_id"
              rules={[
                {
                  required: true,
                  message: "Please select a service type.",
                },
              ]}
            >
              <Select
                showSearch
                // mode="multiple"
                placeholder="Select type"
                options={typeOptions || []}
                open={isOpenTypes}
                onSearch={(value) => setTypeSearch(value)}
                onDropdownVisibleChange={setIsOpenTypes}
                dropdownRender={() => {
                  return (
                    <>
                      {(typeOptions || [])?.length < 1 && (
                        <div className="h-60 mb-2">
                          <div className="flex items-center h-full justify-center">
                            <Empty description="No category found." />
                          </div>
                        </div>
                      )}

                      {(typeOptions || [])?.length > 0 && (
                        <div className="h-60 overflow-y-auto mb-2">
                          {(typeOptions || []).map((item) => (
                            <div
                              key={item.value}
                              className="flex items-center justify-between px-4 py-2 hover:bg-gray-100 cursor-pointer rounded transition-all hover:bg-[rgba(0,_0,_0,_0.04)]"
                              // onClick={() => {
                              //   handleServiceTypeChange(item.value);
                              // }}
                              onClick={() => {
                                form.setFieldValue(
                                  "service_type_id",
                                  item.value,
                                );
                                setIsOpenTypes(false);
                              }}
                            >
                              <span>{item.label}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <Button
                        onClick={() => handleOpen({ type: "TYPE-MANAGE" })}
                        className="border-none text-sm text-theme-gray-30 w-full justify-start h-8 px-4 shadow-none"
                      >
                        <AddSquireIcon />
                        Add New Category
                      </Button>
                    </>
                  );
                }}
              />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  required: true,
                  message: "Please enter email address.",
                },
                { validator: validateEmail },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item label="Organization Logo" name="logo">
              <FileUpload
                name="logo"
                form={form}
                placeholder="Choose file"
                description="JPEG, JPG, PNG (Max 5MB)"
              />
            </Form.Item>

            <div className="col-span-2">
              <Form.Item
                label="Enter full address"
                name="full_address"
                rules={[
                  {
                    required: true,
                    message: "Please enter full address.",
                  },
                ]}
              >
                <TextArea placeholder="Enter full address" />
              </Form.Item>
            </div>
          </div>
        </Form>
      </div>
      <CustomDrawer
        open={open.open}
        onClose={handleClose}
        title="Add Service Type"
      >
        <Form
          className="custom-form"
          layout="vertical"
          // requiredMark="optional"
          size="large"
          form={typeForm}
          onFinish={handleFinish}
        >
          <Form.Item
            name={"service_type_name"}
            label="Category Name"
            rules={[{ required: true, message: "Category name is required," }]}
          >
            <Input size="middle" placeholder="Enter category name" />
          </Form.Item>
          <Space className="flex justify-end">
            <Button
              onClick={() => {
                handleClose();
                typeForm.resetFields();
              }}
              htmlType="button"
              size="small"
              className="button-white-bordered"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              size="small"
              className="button-primary min-w-20"
              loading={creating || updating}
            >
              Save
            </Button>
          </Space>
        </Form>
      </CustomDrawer>
    </div>
  );
};

export default General;
